import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';

import { DesignLayout } from '@common/DesignLayout';
import { usePermission } from '@src/utils/hooks/usePermission';
import { confirmDialogStore } from '@stores/confirmDialog';
import { constantStore } from '@stores/constantStore';
import { counterpartiesStore, TABS } from '@stores/dictionaryStores/counterpartiesStore/counterpartiesStore';
import { counterpartyEditStore } from '@stores/dictionaryStores/counterpartiesStore/counterpartyEditStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';

import { RoutePath } from '../../../AppRouter/constants';
import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../FormWithActions';
import { CounterpartyForm, RUSSIAN_CODE } from './CounterpartyForm';
import { validate } from './validationSchema';

const { COUNTERPARTY_TYPES } = constantStore;

export const CounterpartyEdit = observer(() => {
  const { code, orgLocal, counterpartyTypeCode } = useParams<{
    code: string;
    counterpartyTypeCode: string;
    orgLocal: string;
  }>();
  const { state } = useLocation();

  const { push } = useHistory();
  const { counterparty, setEditMode, setNew, fetch, save, isNew } = counterpartyEditStore;
  const { setTab } = counterpartiesStore;

  useEffect(() => {
    fetchReferences(['counterparties']);
  }, []);

  usePermission('CmPartners', counterpartyEditStore);

  const initialValues = useMemo(() => {
    if (state && state.isCopy) {
      return { ...state.isCopy };
    }

    return counterparty
      ? {
          ...counterparty,
          counterparty: !counterparty?.counterpartyFirstName,
        }
      : {
          isPayAgent: false,
          counterparty: true,
          resident: true,
          thesOksmCode: RUSSIAN_CODE,
          counterpartyTypeCode: COUNTERPARTY_TYPES.DEFAULT_CODE,
          companyCounterparty: false,
          startDate: '01.01.2020',
        };
  }, [counterparty, state]);

  useEffect(() => {
    if (code !== NEW_ENTITY_ID) {
      fetch(code, counterpartyTypeCode, orgLocal);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, code, setNew, setEditMode, state, counterpartyTypeCode, orgLocal]);

  const handleSubmit = useCallback(
    (val) => {
      const values = {
        ...val,
        counterpartyTypeCode: val?.companyCounterparty
          ? COUNTERPARTY_TYPES.DEFAULT_CODE
          : val.counterpartyTypeCode ?? COUNTERPARTY_TYPES.DEFAULT_CODE,
      };

      const validationResult = validate(values)(values);

      if (validationResult) return validationResult;

      (async () => {
        const company = await save({
          ...values,
          isPayAgent: !!values.isPayAgent,
          startDate: values.startDate ? dayjs(new Date(values.startDate)).format('YYYY-MM-DDT00:00:00') : null,
          endDate: values.endDate ? dayjs(new Date(values.endDate)).format('YYYY-MM-DDT00:00:00') : null,
        });
        if (isNew && company) {
          push(
            `${RoutePath.DICTIONARY_COUNTERPARTIES}/${company.code}/${company.counterpartyTypeCode}/${
              company.orgLocal ?? ''
            }`,
          );
        }
      })();

      return undefined;
    },
    [isNew, push, save],
  );

  const beforeSubmitAction = useCallback(
    (values) =>
      new Promise<void>((resolve) => {
        if (!values.companyCounterparty && !values.orgLocal) {
          confirmDialogStore.open({
            text: 'Поле "Принадлежность к справочнику организации" не заполнено. Оставить его пустым?',
            onOk: () => {
              resolve();
            },
          });
        } else {
          resolve();
        }
      }),
    [],
  );

  const goToCounterparties = useCallback(() => {
    setTab(initialValues.companyCounterparty ? TABS.COMPANIES_CM : TABS.COUNTERPARTIES);
    push(RoutePath.DICTIONARY_COUNTERPARTIES);
  }, [initialValues.companyCounterparty, push, setTab]);

  return (
    <DesignLayout title="Справочник: Контрагенты" scrollEnable>
      <FormWrapper onSubmit={handleSubmit} initialValues={initialValues} layout="vertical" compact>
        <FormWithActions
          store={counterpartyEditStore}
          goBackLink={goToCounterparties}
          checkInvalidFields={false}
          beforeSubmitAction={beforeSubmitAction}
        >
          <CounterpartyForm />
        </FormWithActions>
      </FormWrapper>
    </DesignLayout>
  );
});
